var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ShadowGenerator as BABYLON_ShadowGenerator } from '@inter-ikea-betrakta-babylonjs/core/Lights/Shadows/shadowGenerator.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Light } from '../lights/Light.js';
import { assert } from '../../../utility/Assert.js';
import { deep_equals } from '../../../utility/utils.js';
export function is_polygon_offset_bias(bias) {
    return 'z_factor' in bias;
}
export const SHADOW_DEFAULTS = {
    DIRECTIONAL_LIGHT: {
        resolution: 1024,
        near: 0.5,
        far: 20,
        webgl1_bias: {
            bias: 0.005
        },
        webgl2_bias: {
            z_factor: 4.0,
            z_units: 64.0
        }
    },
    SPOT_LIGHT: {
        resolution: 512,
        near: 0.1,
        far: 6,
        webgl1_bias: {
            bias: 0.02
        },
        webgl2_bias: {
            z_factor: 4.0,
            z_units: 64.0
        }
    },
    POINT_LIGHT: {
        resolution: 256,
        near: 0.1,
        far: 10,
        webgl1_bias: {
            bias: 0.02
        },
        webgl2_bias: {
            z_factor: 4.0,
            z_units: 64.0
        }
    }
};
/**
 * An abstract controller for a light's shadows.
 */
export class ShadowController {
    constructor(_light, _babylon_light, _light_system, _shadow_resolution, _shadow_near, _shadow_far, bias) {
        this._light = _light;
        this._babylon_light = _babylon_light;
        this._light_system = _light_system;
        this._shadow_resolution = _shadow_resolution;
        this._shadow_near = _shadow_near;
        this._shadow_far = _shadow_far;
        this._enable_shadows = false;
        this._babylon_shadow_generator = null;
        // State we store so we can properly clean up old shadowmaps when they get switched out (Babylon creates new ones when changing certain settings).
        // See function: setup_mesh_rendering_filter
        this._current_shadowmap = null;
        this._current_shadowmap_original_custom_is_ready_function = null;
        this._babylon_light.shadowEnabled = this._enable_shadows;
        this._shadow_casters = _light_system.shadow_casters;
        this._bias = Object.assign({}, bias);
    }
    dispose() {
        var _a;
        this.setup_mesh_rendering_filter(null);
        (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.dispose();
        this._babylon_shadow_generator = null;
    }
    update_bias_properties() {
        if (this._babylon_shadow_generator === null)
            return;
        if (is_polygon_offset_bias(this._bias)) {
            this._babylon_shadow_generator.bias = 0.0;
        }
        else {
            this._babylon_shadow_generator.bias = this._bias.bias;
        }
    }
    update(meshes_modifications) {
        this._babylon_light.shadowEnabled = this._enable_shadows;
        if (this._babylon_shadow_generator === null && this._enable_shadows) {
            // The shadowmap needs to be created
            this._babylon_shadow_generator = this.create_babylon_shadow_generator();
            this.update_bias_properties();
            // Unsure why we need to do this, but might have to do with applying the shadowmap to materials
            this._light_system.viewer.mark_scene_dirty();
        }
        else if (this._babylon_shadow_generator !== null && !this._enable_shadows) {
            // We should dispose the shadowmap
            this.setup_mesh_rendering_filter(null);
            this._babylon_shadow_generator.dispose();
            this._babylon_shadow_generator = null;
        }
        if (this._light.is_dirty(Light.DIRTY_SHADOW_NEAR | Light.DIRTY_ENABLE_SHADOWS)) {
            this._babylon_light.shadowMinZ = this._shadow_near;
        }
        if (this._light.is_dirty(Light.DIRTY_SHADOW_FAR | Light.DIRTY_ENABLE_SHADOWS)) {
            this._babylon_light.shadowMaxZ = this._shadow_far;
        }
        if (this._light.is_dirty(Light.DIRTY_SHADOW_BIAS)) {
            this.update_bias_properties();
        }
        if (this._light.is_dirty(Light.DIRTY_SHADOW_RESOLUTION | Light.DIRTY_ENABLE_SHADOWS) && this._babylon_shadow_generator !== null) {
            const shadowmap = this._babylon_shadow_generator.getShadowMap();
            assert(shadowmap, 'Shadowmap is not initialized');
            shadowmap.resize({ width: this._shadow_resolution, height: this._shadow_resolution });
        }
        const shadowmap_needs_update = this._light.is_dirty(Light.DIRTY_SHADOW_NEAR |
            Light.DIRTY_SHADOW_FAR |
            Light.DIRTY_SHADOW_RESOLUTION |
            Light.DIRTY_SHADOW_MAP |
            Light.DIRTY_ENABLE_SHADOWS);
        if (shadowmap_needs_update || this.should_update_shadowmap(meshes_modifications)) {
            this.update_babylon_shadowmap();
        }
    }
    add_shadowcaster(scene_object) {
        this.add_babylon_shadowcaster(scene_object)
            .then(() => this.update_babylon_shadowmap());
    }
    remove_shadowcaster(scene_object) {
        this.remove_babylon_shadowcaster(scene_object)
            .then(() => this.update_babylon_shadowmap());
    }
    /**
     * A per-mesh filter that allows us to do dynamic filter of objects to be rendered in the shadowmap.
     * Do remember that if you change something for the predicate, you have to dirty the shadowmap for it to re-render.
     * @param mesh The mesh to be rendered or not.
     */
    mesh_should_render(mesh) {
        return (mesh.layerMask & this._light_system.viewer.get_shadows_render_layer_mask().bitmap) !== 0;
    }
    /**
     * Sets up our custom mesh rendering filter, allowing us to have render layers for shadowmaps.
     * Currently only used with a global layer mask from the Viewer
     * @param shadowmap The shadowmap currently rendered with. Early outs if it's the same as previously.
     */
    setup_mesh_rendering_filter(shadowmap) {
        // Babylon shadow generators do have a user-settable predicate to decide if a given sub mesh should be rendered or not.
        // However, we can't retrace the original mesh from the submesh (seems to be due to our cloning.), so we have to do it on the customIsReadyFunction on the render target.
        // In Babylon's current implementation, it is just a "return true", but for futureproofing we're also calling their function.
        // Early out if nothing has changed since last time
        if (shadowmap === this._current_shadowmap)
            return;
        if (this._current_shadowmap !== null) {
            // Clean up the old shadowmap, restoring its customIsReadyFunction
            this._current_shadowmap.customIsReadyFunction = this._current_shadowmap_original_custom_is_ready_function;
            this._current_shadowmap_original_custom_is_ready_function = null;
        }
        this._current_shadowmap = shadowmap;
        if (shadowmap !== null) {
            // If the new shadowmap exists, store away its original customIsReadyFunction and set up the new predicate.
            this._current_shadowmap_original_custom_is_ready_function = shadowmap.customIsReadyFunction;
            shadowmap.customIsReadyFunction = (mesh, refresh_rate, pre_warm) => {
                return this.mesh_should_render(mesh) && this._current_shadowmap_original_custom_is_ready_function(mesh, refresh_rate, pre_warm);
            };
        }
    }
    render() {
        var _a, _b, _c;
        const shadowmap = (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.getShadowMap();
        if (shadowmap === null || shadowmap === void 0 ? void 0 : shadowmap._shouldRender()) {
            this.setup_mesh_rendering_filter(shadowmap);
            this._babylon_light.getScene().incrementRenderId();
            (_c = (_b = this._babylon_shadow_generator) === null || _b === void 0 ? void 0 : _b.getShadowMap()) === null || _c === void 0 ? void 0 : _c.render(false, false);
        }
    }
    get enable_shadows() {
        return this._enable_shadows;
    }
    set enable_shadows(value) {
        if (this._enable_shadows === value)
            return;
        if (value && !this._light_system.can_enable_shadow()) {
            console.warn(`Unable to enable shadow for light with id: ${this._light.id}. Maximum number of shadowcasting lights reached`);
            return;
        }
        this._enable_shadows = value;
        this._light.mark_dirty(Light.DIRTY_ENABLE_SHADOWS);
    }
    get_bias() {
        return Object.assign({}, this._bias);
    }
    set_bias(bias) {
        if (deep_equals(this._bias, bias)) {
            return;
        }
        this._bias = Object.assign({}, bias);
        this._light.mark_dirty(Light.DIRTY_SHADOW_BIAS | Light.DIRTY_SHADOW_MAP);
    }
    get shadow_near() {
        return this._shadow_near;
    }
    set shadow_near(value) {
        if (this._shadow_near === value)
            return;
        this._shadow_near = value;
        this._light.mark_dirty(Light.DIRTY_SHADOW_NEAR);
    }
    get shadow_far() {
        return this._shadow_far;
    }
    set shadow_far(value) {
        if (this._shadow_far === value)
            return;
        this._shadow_far = value;
        this._light.mark_dirty(Light.DIRTY_SHADOW_FAR);
    }
    get shadow_resolution() {
        return this._shadow_resolution;
    }
    set shadow_resolution(value) {
        if (this._shadow_resolution === value)
            return;
        this._shadow_resolution = value;
        this._light.mark_dirty(Light.DIRTY_SHADOW_RESOLUTION);
    }
    get shadow_view_matrix() {
        var _a;
        return (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.getTransformMatrix();
    }
    update_babylon_shadowmap() {
        var _a, _b;
        (_b = (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.getShadowMap()) === null || _b === void 0 ? void 0 : _b.resetRefreshCounter();
    }
    create_babylon_shadow_generator() {
        // With WebGL 2, we can optimize Babylon shadowmap rendering by supplying this flag.
        const use_red_rendertarget = this._babylon_light.getEngine().webGLVersion === 2;
        // This only seems necessary in WebGL1 for some reason, otherise we get artifacts.
        const use_full_float = this._babylon_light.getEngine().webGLVersion === 1;
        const generator = new BABYLON_ShadowGenerator(this._shadow_resolution, this._babylon_light, use_full_float, undefined, use_red_rendertarget);
        generator.transparencyShadow = false;
        generator.usePercentageCloserFiltering = true;
        // Due to a Babylon bug - this actually means we always render front faces.
        // (Babylon doesn't seem to care about us being right-handed in shadow-rendering)
        generator.forceBackFacesOnly = true;
        generator.bias = 0.0;
        generator.normalBias = 0.0;
        const engine = this._babylon_light.getEngine();
        let restore_z_offset = 0.0;
        let restore_z_offset_units = 0.0;
        generator.onBeforeShadowMapRenderObservable.add(() => {
            const state = engine.depthCullingState;
            restore_z_offset = state.zOffset;
            restore_z_offset_units = state.zOffsetUnits;
            let z_factor = 0.0;
            let z_units = 0.0;
            if (is_polygon_offset_bias(this._bias)) {
                z_factor = this._bias.z_factor;
                z_units = this._bias.z_units;
            }
            engine.setZOffset(z_factor);
            engine.setZOffsetUnits(z_units);
        });
        generator.onAfterShadowMapRenderObservable.add(() => {
            engine.setZOffset(restore_z_offset);
            engine.setZOffsetUnits(restore_z_offset_units);
        });
        if (is_polygon_offset_bias(this._bias)) {
            generator.bias = 0.0;
        }
        else {
            generator.bias = this._bias.bias;
        }
        this._babylon_light.shadowMinZ = this._shadow_near;
        this._babylon_light.shadowMaxZ = this._shadow_far;
        this._light.unset_dirty_flags(Light.DIRTY_SHADOW_RESOLUTION |
            Light.DIRTY_SHADOW_NEAR |
            Light.DIRTY_SHADOW_FAR |
            Light.DIRTY_SHADOW_BIAS |
            Light.DIRTY_ENABLE_SHADOWS);
        this._shadow_casters.forEach(object => this.add_shadowcaster(object));
        const shadowmap = generator.getShadowMap();
        assert(shadowmap, 'Shadowmap is not initialized');
        // We want to manualy control shadowmap render
        shadowmap.refreshRate = BABYLON_RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
        return generator;
    }
    add_babylon_shadowcaster(scene_object) {
        return __awaiter(this, void 0, void 0, function* () {
            yield scene_object.ready;
            scene_object.mesh_iterator(false, (child) => {
                var _a;
                (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.addShadowCaster(child, true);
            });
        });
    }
    remove_babylon_shadowcaster(scene_object) {
        return __awaiter(this, void 0, void 0, function* () {
            yield scene_object.ready;
            scene_object.mesh_iterator(false, (child) => {
                var _a;
                (_a = this._babylon_shadow_generator) === null || _a === void 0 ? void 0 : _a.removeShadowCaster(child, true);
            });
        });
    }
}
//# sourceMappingURL=ShadowController.js.map